import classnames from 'classnames'
import React, { ReactNode, useCallback, useMemo, useRef, useState } from 'react'

import useClickOutsideV2 from '@src/quince-site-common/src/hooks/useClickOutsideV2'
import tick from './icons/check_circle.svg'
import { noop } from '@src/utils/functions/utils'

import Icon from '../icon'

import ChevronDown from './icons/ChevronDown.inline.svg'
import styles from './selectV2.module.scss'

export interface TOption {
  label: ReactNode
  value: string
}

export interface SelectProps {
  id: string
  options: TOption[]
  selectedOption: string
  onOptionClick: (e: React.MouseEvent<HTMLDivElement>, value: string) => void
}

const OptionRenderer = ({
  option,
  rightIcon,
  onClick = noop,
  className,
}: {
  option: TOption
  rightIcon?: ReactNode
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void
  className?: string
}) => {
  return (
    <div className={classnames(styles.wrapper__body__item, className)} onClick={onClick}>
      <div className={styles.wrapper__body__item__text}>{option.label}</div>
      {rightIcon}
    </div>
  )
}

const SelectV2 = ({ id, options = [], selectedOption, onOptionClick }: SelectProps) => {
  const [isListOpen, setIsListOpen] = useState(false)
  const selectRef = useRef<HTMLDivElement>(null)
  const optionWrapperRef = useRef<HTMLDivElement>(null)

  const onSelectClick = () => {
    setIsListOpen(prev => !prev)
  }

  const onOutsideClick = useCallback(() => {
    setIsListOpen(false)
  }, [])
  const outsideClickTrackerRefs = useMemo(() => [selectRef, optionWrapperRef], [selectRef, optionWrapperRef])
  useClickOutsideV2(outsideClickTrackerRefs, onOutsideClick)

  return (
    <div id={id} className={classnames(styles.wrapper)} ref={selectRef} onClick={onSelectClick}>
      <OptionRenderer
        className={styles.wrapper__header}
        option={options.find(o => o.value === selectedOption) ?? { label: '', value: '' }}
        rightIcon={
          options.length > 1 ? (
            <Icon iconSvg={ChevronDown} className={classnames(styles.chevronIcon, isListOpen ? styles.flip : '')} />
          ) : (
            <Icon iconSvg={tick} className={styles.tickIcon} />
          )
        }
      />

      {options.length > 1 ? (
        <div
          ref={optionWrapperRef}
          className={classnames(styles.wrapper__body, {
            [styles.hide ?? '']: !isListOpen,
          })}
        >
          {options.map(option => (
            <OptionRenderer
              key={option.value}
              option={option}
              onClick={e => {
                onOptionClick(e, option.value)
              }}
            />
          ))}
        </div>
      ) : null}
    </div>
  )
}

export default SelectV2
